import React, { Fragment, useState, useEffect, useContext, useRef } from 'react';
import { Divider, Space, Form, Checkbox, Input, Radio, Button, Typography, Segmented, Select, Col, Row, Skeleton, notification, Alert, Popconfirm, message, Tabs, Switch } from 'antd';
import { QuestionCircleOutlined, SaveOutlined, UserOutlined, TruckOutlined, BankOutlined, GlobalOutlined } from '@ant-design/icons';
import { PiShootingStarDuotone } from 'react-icons/pi';
/*
| Multiple contacts
*/
import ContactNumbers from '../../shared-contact-components/ContactNumbers';
import EmailAddresses from '../../shared-contact-components/EmailAddresses';
import Addresses from '../../shared-contact-components/Addresses';

import WebPages from '../../shared-contact-components/WebPages';


import CustomerService from '../services';
import CustomerShippingForm from './CustomerShippingForm';
import CustomerCustomFields from './partials/CustomerCustomFields';
import SettingsService from '../../Settings/services/';
import PageExceptions from 'components/common/PageExceptions/PageExceptions';
import NoAccessAlertWarning from 'components/common/PageExceptions/NoAccessAlertWarning';

import AppContext  from '../../../context/Context';
import AuthContext from '../../../context/authProvider';
import { useAuthSettingUtils } from '../../../helpers/settings/settingUtils';

const customerSettingIsOn = (value) => value === true || value === 1 || value === '1';
const normalizeCustomerType = (value) => (value === 'individual' ? 'individual' : 'business');

/** Master lookup IDs from the API must match Select option values (numbers) for labels to resolve. */
function normalizeMasterId(value) {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
}

/** Ant Design Select values must be primitives — never use objects as option values. */
const PAYMENT_TERMS_SELECTOR_ORG = 'org_default';

function structuredToPaymentTermsSelectorKey(type, days, eomOffset) {
    if (!type) return PAYMENT_TERMS_SELECTOR_ORG;
    if (type === 'days') return `days_${days ?? 0}`;
    if (type === 'eom') return `eom_${eomOffset ?? 0}`;
    return PAYMENT_TERMS_SELECTOR_ORG;
}

function paymentTermsSelectorKeyToStructured(key) {
    if (!key || key === PAYMENT_TERMS_SELECTOR_ORG) {
        return { payment_terms_type: null, payment_terms_days: null, payment_terms_eom_offset_months: null };
    }
    if (String(key).startsWith('days_')) {
        const d = parseInt(String(key).slice(5), 10);
        return {
            payment_terms_type: 'days',
            payment_terms_days: Number.isNaN(d) ? 0 : d,
            payment_terms_eom_offset_months: null,
        };
    }
    if (String(key).startsWith('eom_')) {
        const m = parseInt(String(key).slice(4), 10);
        return {
            payment_terms_type: 'eom',
            payment_terms_days: null,
            payment_terms_eom_offset_months: Number.isNaN(m) ? 0 : m,
        };
    }
    return { payment_terms_type: null, payment_terms_days: null, payment_terms_eom_offset_months: null };
}

const CustomerForm = ({ id, segment = 'general', customerFormCallBack }) => {
    const [form] = Form.useForm();
    const shippingFormRef = useRef(null);
    const customerType = Form.useWatch('customer_type', form);
    
    const appContext  = useContext(AppContext);
    const authContext = useContext(AuthContext);
    const { getAuthSetting } = useAuthSettingUtils();


    const [pageStatusCode, setPageStatusCode] = useState(200);
    const [customer, setCustomer] = useState({
        status         : 'active', // Changed from true to 'active'
        first_name     : '',
        last_name      : '',
        gender         : 'male',
        customer_type  : 'individual',
        business_name  : '',
        business_registration_number: '',
        business_size  : '',
        business_type  : '',
        tax_id         : '',
        tax_exempt     : false,
        tax_exempt_number: '',
        credit_limit   : 0.00,
        payment_terms_type: null,
        payment_terms_days: null,
        payment_terms_eom_offset_months: null,
        preferred_contact_method : 'email',
        marketing_opt_in : false,
        contact_numbers: [],
        emails         : [],
        web_pages      : [],
        addresses      : [],
        shippings      : [],
        description  : '',
        custom_fields  : [],
    });
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [currentSegment, setCurrentSegment] = useState(segment);
    const [displayShippingAccount, setDisplayShippingAccount] = useState(false);
    const [businessSizes, setBusinessSizes] = useState([]);
    const [businessTypes, setBusinessTypes] = useState([]);
    


    useEffect(() => {
        // Custom fields settings are now included in the customer resource response
        // For new customers, we still need to fetch settings to initialize custom fields
        if (!id) {
            SettingsService.getCustomerCustomFields().then((res) => {
                try {
                    if (res.data && res.data.success && res.data.data) {
                        const settingFields = res.data.data || [];
                        // Initialize custom fields from settings for new customer
                        setCustomer(prev => ({
                            ...prev,
                            custom_fields: settingFields.map(field => ({
                                title: field.title,
                                value: '',
                                display_internal: field.display_internal,
                                display_external: field.display_external
                            }))
                        }));
                    }
                } catch(error) {
                    console.log(error);
                }
            });

            // Blueprint from API: provides org default due days for UI ("Default (Organization)").
            CustomerService.getCustomerById(-1).then((response) => {
                try {
                    const result = response.data;
                    if (result?.success && result?.data) {
                        const b = result.data;
                        const orgDueDays = b?.extra?.meta?.sale_due_date_days;
                        if (orgDueDays != null) {
                            setCustomer(prev => ({
                                ...prev,
                                extra: {
                                    ...(prev.extra || {}),
                                    meta: {
                                        ...((prev.extra || {}).meta || {}),
                                        sale_due_date_days: orgDueDays,
                                    },
                                },
                            }));
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
            });
        }

        // Load business sizes and types from API
        const loadBusinessData = async () => {
            try {
                const [sizesResponse, typesResponse] = await Promise.all([
                    CustomerService.getBusinessSizes(),
                    CustomerService.getBusinessTypes()
                ]);
                
                if (sizesResponse.data.success) {
                    setBusinessSizes(sizesResponse.data.data.map(size => ({
                        value: normalizeMasterId(size.id),
                        label: size.name
                    })));
                }
                
                if (typesResponse.data.success) {
                    setBusinessTypes(typesResponse.data.data.map(type => ({
                        value: normalizeMasterId(type.id),
                        label: type.name
                    })));
                }
            } catch (error) {
                console.error('Failed to load business data:', error);
            }
        };

        loadBusinessData();

        // Load customer data if editing
        if (id) {
            getPageData();
        } else {
            setIsPageLoading(false);
        }
    }, [id]);

    const getPageData = () => {
        CustomerService.getCustomerById(id).then((response) => {
            try {
                let result = response.data;
                if(result.success) {
                    if(result?.data) {
                        let customerData = result.data;
                        customerData.customer_type = normalizeCustomerType(customerData.customer_type);

                        // Initialize arrays if they don't exist
                        customerData.status = customerData.status || 'active'; // Changed from true to 'active'
                        customerData.contact_numbers = customerData.contact_numbers || [];
                        customerData.emails = customerData.emails || [];
                        customerData.web_pages = customerData.web_pages || [];
                        // `defaultAddress` was referenced here but never defined or imported —
                        // a ReferenceError that only fired for a customer with ZERO address
                        // rows. Every staff-created customer gets one (the Addresses editor
                        // always submits a row), so it lay dormant until customers started
                        // arriving from the public sign-up form, which only writes an address
                        // when the visitor actually gave one.
                        //
                        // No replacement default is needed: Addresses builds its own blank row
                        // from the organization's country/state when initialValues is empty.
                        customerData.addresses = customerData.addresses?.length ? customerData.addresses : [];
                        customerData.shippings = customerData.shippings || [];
                        
                        // Custom fields already include all settings fields merged with customer values
                        // No need to merge - they come pre-merged from the backend
                        setCustomer(customerData);
                        setIsPageLoading(false);
                        
                        // Set displayShippingAccount to true if shipping already exists
                        if (customerData.shippings && customerData.shippings.length > 0 && customerData.shippings[0]?.id) {
                            setDisplayShippingAccount(true);
                        }
                        
                        // Update form values with loaded data
                        form.setFieldsValue({
                            status: customerData.status || 'active',
                            first_name: customerData.first_name,
                            last_name: customerData.last_name,
                            gender: customerData.gender || 'male',
                            customer_type: normalizeCustomerType(customerData.customer_type || 'individual'),
                            business_name: customerData.business_name,
                            business_registration_number: customerData.business_registration_number,
                            business_size: normalizeMasterId(customerData.business_size),
                            business_type: normalizeMasterId(customerData.business_type),
                            tax_id: customerData.tax_id,
                            tax_exempt: !!customerData.tax_exempt,
                            tax_exempt_number: customerData.tax_exempt_number || '',
                            credit_limit: customerData.credit_limit,
                            payment_terms_type: customerData.payment_terms_type,
                            payment_terms_days: customerData.payment_terms_days,
                            payment_terms_eom_offset_months: customerData.payment_terms_eom_offset_months,
                            payment_terms_selector: structuredToPaymentTermsSelectorKey(
                                customerData.payment_terms_type,
                                customerData.payment_terms_days,
                                customerData.payment_terms_eom_offset_months
                            ),
                            preferred_contact_method: customerData.preferred_contact_method || 'email',
                            marketing_opt_in: customerData.marketing_opt_in,
                            description: customerData.description
                        });
                        
                        setIsPageLoading(false);
                    }
                } else {
                    setPageStatusCode(result.status);
                    setIsPageLoading(false);
                }
            } catch (error) {
                console.log(error);
            }
        });
    };

    // Select options load async; if we set business_type / business_size before options exist, Ant Design shows the raw id.
    useEffect(() => {
        if (isPageLoading || !id) return;
        if (normalizeCustomerType(customer.customer_type) !== 'business') return;

        const patch = {};
        if (businessTypes.length > 0 && customer.business_type != null && customer.business_type !== '') {
            patch.business_type = normalizeMasterId(customer.business_type);
        }
        if (businessSizes.length > 0 && customer.business_size != null && customer.business_size !== '') {
            patch.business_size = normalizeMasterId(customer.business_size);
        }
        if (Object.keys(patch).length > 0) {
            form.setFieldsValue(patch);
        }
    }, [
        isPageLoading,
        id,
        businessTypes,
        businessSizes,
        customer.customer_type,
        customer.business_type,
        customer.business_size,
        form,
    ]);

    const handleCustomerOnChange = (target) => {
        setCustomer(prev => ({
            ...prev,
            [target.name]: target.value
        }));
    };

    const handleCustomFieldsChange = (customFields) => {
        setCustomer(prev => ({
            ...prev,
            custom_fields: customFields
        }));
    };

    const handleCustomerTypeChange = (value) => {
        setCustomer(prev => ({
            ...prev,
            customer_type: value,
            is_company: value === 'business',
            // Reset business fields if switching away from business
            business_name: value === 'business' ? prev.business_name : '',
            business_size: value === 'business' ? prev.business_size : null,
            business_type: value === 'business' ? prev.business_type : null
        }));

        // Keep AntD Form values in sync with local state updates
        form.setFieldsValue({ customer_type: value });
        if (value !== 'business') {
            form.setFieldsValue({
                business_name: '',
                business_size: null,
                business_type: null,
            });
        }
    };




    const handleSaveAll = (values) => {
        // Get current shipping data from CustomerShippingForm if it exists and displayShippingAccount is true
        let shippingData = [];
        
        // Check if customer has existing shipping (even if displayShippingAccount wasn't set properly)
        const hasExistingShipping = customer.shippings && customer.shippings.length > 0 && customer.shippings[0]?.id;
        
        if (!displayShippingAccount && !hasExistingShipping) {
            // If shipping account is same as primary and no existing shipping, send empty array
            shippingData = [];
        } else if (displayShippingAccount && shippingFormRef.current) {
            // Get current shipping data from the form
            const currentShipping = shippingFormRef.current.getShippingData();
            // Check if shipping data exists and has meaningful content (not just empty strings)
            const hasShippingData = currentShipping && (
                currentShipping.first_name || 
                currentShipping.last_name || 
                currentShipping.address_line_1 ||
                currentShipping.contact_number ||
                hasExistingShipping
            );
            
            if (hasShippingData) {
                // Include shipping if it has any data or if there was existing shipping
                shippingData = [currentShipping];
            } else if (hasExistingShipping) {
                // Fallback to existing shipping if form data is not valid
                shippingData = customer.shippings;
            }
        } else if (hasExistingShipping) {
            // If shipping account exists (even if displayShippingAccount wasn't set), use existing shipping
            shippingData = customer.shippings;
        } else if (displayShippingAccount && shippingFormRef.current) {
            // If displayShippingAccount is true but no existing shipping, try to get from form
            const currentShipping = shippingFormRef.current.getShippingData();
            if (currentShipping && (currentShipping.first_name || currentShipping.address_line_1)) {
                shippingData = [currentShipping];
            }
        }

        const customerData = {
            ...customer,
            ...values,
            shippings: shippingData
        };
        // UI-only selector; do not send to API
        delete customerData.payment_terms_selector;

        const savePromise = customerData.id ? 
            CustomerService.updateCustomer(customerData.id, customerData) :
            CustomerService.createCustomer(customerData);

        savePromise
            .then((response) => {
                const result = response.data;
                if (result.success) {
                    message.success(result.message);
                    customerFormCallBack?.(result.data);
                } else {
                    message.error(result.message || 'Failed to submit data');
                }
            })
            .catch((error) => {
                console.error(error);
                message.error('An error occurred while saving');
            });
    };

    if(pageStatusCode !== 200) {
        return <PageExceptions statusCode={pageStatusCode} />;
    }

    if (isPageLoading) {
        return <Skeleton className='m-3' active />;
    }

    const {
        first_name,
        last_name,
        is_company,
        contact_numbers,
        emails,
        web_pages,
        addresses = [],
        gender
    } = customer;


    const { Text, Title }   = Typography;
    const { config }        = appContext;
    const { permissions }   = authContext.auth.user.extra.privileges;

    const requireCustomerAddress = customerSettingIsOn(getAuthSetting('customer_require_address'));
    const requireCustomerEmail = customerSettingIsOn(getAuthSetting('customer_require_email'));
    const requireCustomerContactNumber = customerSettingIsOn(getAuthSetting('customer_require_contact_number'));

    return (
        <Fragment>
            {!permissions.includes('customers.create') || !permissions.includes('customers.edit') ? 
                <NoAccessAlertWarning  />
            : null}

            <div className="p-1">
                <Form
                    form={form}
                    layout="vertical"
                    // style={{ maxWidth: 600, margin: 'auto' }}
                    // size='large'
                    onFinish={handleSaveAll}
                    autoComplete="off"
                    initialValues={{
                        status: customer.status || 'active',
                        first_name: customer.first_name,
                        last_name: customer.last_name,
                        gender: customer.gender || 'male',
                        customer_type: customer.customer_type || 'individual',
                        business_name: customer.business_name,
                        business_registration_number: customer.business_registration_number,
                        business_size: normalizeMasterId(customer.business_size),
                        business_type: normalizeMasterId(customer.business_type),
                        tax_id: customer.tax_id,
                        tax_exempt: !!customer.tax_exempt,
                        tax_exempt_number: customer.tax_exempt_number || '',
                        credit_limit: customer.credit_limit,
                        payment_terms_type: customer.payment_terms_type,
                        payment_terms_days: customer.payment_terms_days,
                        payment_terms_eom_offset_months: customer.payment_terms_eom_offset_months,
                        payment_terms_selector: structuredToPaymentTermsSelectorKey(
                            customer.payment_terms_type,
                            customer.payment_terms_days,
                            customer.payment_terms_eom_offset_months
                        ),
                        preferred_contact_method: customer.preferred_contact_method || 'email',
                        marketing_opt_in: customer.marketing_opt_in,
                        description: customer.description
                    }}
                >
                    <Tabs
                        activeKey={currentSegment}
                        onChange={(key) => setCurrentSegment(key)}
                        size="small"
                        className="mb-4"
                        items={[
                            {
                                key: 'general',
                                label: (
                                    <span>
                                        General
                                    </span>
                                ),
                                children: (
                                    <Fragment>
                                        <Form.Item 
                                            label="Status" 
                                            name="status"
                                            rules={[{ required: true, message: 'Status is required' }]}
                                        >
                                            <Select
                                                name="status"
                                                onChange={(value) => handleCustomerOnChange({ name: 'status', value })}
                                                options={[
                                                    { value: 'active', label: 'Active' },
                                                    { value: 'hold', label: 'Hold' },
                                                    { value: 'inactive', label: 'Inactive' }
                                                ]}
                                            />
                                        </Form.Item>

                                        <Divider titlePlacement="start" plain>Customer Type</Divider>

                                        <Form.Item label="Customer Type" name="customer_type">
                                            <Select
                                                name="customer_type"
                                                onChange={handleCustomerTypeChange}
                                                options={[
                                                    { value: 'individual', label: 'Individual' },
                                                    { value: 'business', label: 'Business' }
                                                ]}
                                            />
                                        </Form.Item>

                                        {customerType === 'business' && (
                                            <Fragment>
                                                <Divider titlePlacement="start" plain>Business Information</Divider>

                                                <Row gutter={12}>
                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            label="Business Name"
                                                            name="business_name"
                                                            rules={[{ required: true, message: 'Business name is required for business customers' }]}
                                                        >
                                                            <Input
                                                                name="business_name"
                                                                onChange={({ target }) => handleCustomerOnChange(target)}
                                                                placeholder="e.g. Acme LLC"
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            label="Business No"
                                                            name="business_registration_number"
                                                            rules={[{ required: true, message: 'Business registration number is required for business customers' }]}
                                                        >
                                                            <Input
                                                                name="business_registration_number"
                                                                onChange={({ target }) => handleCustomerOnChange(target)}
                                                                placeholder="e.g. CR / Company No."
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            label="Business Type"
                                                            name="business_type"
                                                            rules={[{ required: true, message: 'Business type is required for business customers' }]}
                                                        >
                                                            <Select
                                                                name="business_type"
                                                                onChange={(value) => handleCustomerOnChange({ name: 'business_type', value })}
                                                                placeholder="Select business type"
                                                                options={businessTypes}
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item label="Business Size" name="business_size">
                                                            <Select
                                                                name="business_size"
                                                                onChange={(value) => handleCustomerOnChange({ name: 'business_size', value })}
                                                                placeholder="Select business size"
                                                                options={businessSizes}
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Fragment>
                                        )}

                                        <Divider titlePlacement="start" plain>Primary Contact</Divider>

                                        <Row gutter={12}>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    label="First Name"
                                                    name="first_name"
                                                    rules={[
                                                        ({ getFieldValue }) => ({
                                                            required: getFieldValue('customer_type') === 'individual',
                                                            message: 'First Name is required for individual customers',
                                                        }),
                                                    ]}
                                                >
                                                    <Input name="first_name" onChange={({ target }) => handleCustomerOnChange(target)} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    label="Last Name"
                                                    name="last_name"
                                                >
                                                    <Input name="last_name" onChange={({ target }) => handleCustomerOnChange(target)} />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item 
                                            name="gender" 
                                            label="Gender"
                                            rules={[
                                                ({ getFieldValue }) => ({
                                                    required: getFieldValue('customer_type') === 'individual',
                                                    message: 'Gender is required for individual customers',
                                                }),
                                            ]}
                                        >
                                            <Radio.Group
                                                name="gender"
                                                onChange={({ target }) => handleCustomerOnChange(target)}
                                            >
                                                <Radio value="male">Male</Radio>
                                                <Radio value="female">Female</Radio>
                                                <Radio value="other">Other</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        <Divider titlePlacement="start" plain>Custom Fields</Divider>
                                        
                                        <CustomerCustomFields 
                                            custom_fields={customer.custom_fields || []} 
                                            handleCustomFieldsChange={handleCustomFieldsChange} 
                                        />

                                        <Row gutter={12}>
                                            <Col xs={24} md={12}>
                                                <Form.Item label="Preferred Contact" name="preferred_contact_method">
                                                    <Select
                                                        name="preferred_contact_method"
                                                        onChange={(value) => handleCustomerOnChange({ name: 'preferred_contact_method', value })}
                                                        options={[
                                                            { value: 'email', label: 'Email' },
                                                            { value: 'phone', label: 'Phone' },
                                                            { value: 'sms', label: 'SMS' },
                                                            { value: 'mail', label: 'Mail' }
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item label="Marketing Communications" name="marketing_opt_in" valuePropName="checked">
                                                    <Switch
                                                        className="ms-2"
                                                        name="marketing_opt_in"
                                                        onChange={(checked) => handleCustomerOnChange({ name: 'marketing_opt_in', value: checked })}
                                                        checkedChildren="YES"
                                                        unCheckedChildren="NO"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item label="Description" name="description">
                                            <Input.TextArea name="description" onChange={({ target }) => handleCustomerOnChange(target)} />
                                        </Form.Item>

                                        <Divider titlePlacement="start" plain>Contact Numbers</Divider>
                                        <Form.Item label="contact">
                                            <ContactNumbers
                                                name="contact_numbers"
                                                initialValues={contact_numbers}
                                                requireContactNumber={requireCustomerContactNumber}
                                            />
                                        </Form.Item>

                                        <Divider titlePlacement="start" plain>Email Addresses</Divider>
                                        <Form.Item label="Email">
                                            <EmailAddresses 
                                                name="emails"
                                                initialValues={emails}
                                                isRequired={requireCustomerEmail}
                                            />
                                        </Form.Item>

                                        <Divider titlePlacement="start" plain>Address</Divider>
                                        <Addresses
                                            name="addresses"
                                            multiple={false}
                                            initialValues={addresses}
                                            requireAddressLine1={requireCustomerAddress}
                                        />
                                    </Fragment>
                                )
                            },
                            ...(customerType === 'business'
                                ? [
                                    {
                                        key: 'business',
                                        label: (
                                            <span>
                                                Tax & Billing
                                            </span>
                                        ),
                                        children: (
                                            <Fragment>
                                                <Form.Item label="Tax ID/VAT" name="tax_id">
                                                    <Input
                                                        name="tax_id"
                                                        onChange={({ target }) => handleCustomerOnChange(target)}
                                                        placeholder="Tax identification number"
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Tax exempt"
                                                    name="tax_exempt"
                                                    valuePropName="checked"
                                                    tooltip="When enabled, invoices and estimates issued to this customer will skip tax calculation entirely. Use for resellers with valid certificates, registered charities, and other tax-exempt entities."
                                                >
                                                    <Switch
                                                        onChange={(checked) => handleCustomerOnChange({ name: 'tax_exempt', value: checked })}
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prev, curr) => prev.tax_exempt !== curr.tax_exempt}
                                                >
                                                    {() =>
                                                        form.getFieldValue('tax_exempt') ? (
                                                            <Form.Item
                                                                label="Exemption certificate #"
                                                                name="tax_exempt_number"
                                                                tooltip="Resale certificate (US), charity number (UK), NFP registration (AU), etc. Recorded for audit purposes."
                                                            >
                                                                <Input
                                                                    name="tax_exempt_number"
                                                                    onChange={({ target }) => handleCustomerOnChange(target)}
                                                                    placeholder="e.g. RC123456789"
                                                                    maxLength={100}
                                                                />
                                                            </Form.Item>
                                                        ) : null
                                                    }
                                                </Form.Item>

                                                <Divider titlePlacement="start" plain>Billing</Divider>

                                                <Row gutter={12}>
                                                    <Col xs={24} md={12}>
                                                        {/* Hidden structured fields (submitted to API). NULL means "use org default". */}
                                                        <Form.Item name="payment_terms_type" hidden>
                                                            <Input />
                                                        </Form.Item>
                                                        <Form.Item name="payment_terms_days" hidden>
                                                            <Input />
                                                        </Form.Item>
                                                        <Form.Item name="payment_terms_eom_offset_months" hidden>
                                                            <Input />
                                                        </Form.Item>

                                                        <Form.Item
                                                            label="Payment Terms"
                                                            name="payment_terms_selector"
                                                            tooltip={
                                                                customer?.extra?.meta?.sale_due_date_days != null
                                                                    ? `Default (Organization): Net ${customer.extra.meta.sale_due_date_days}`
                                                                    : 'Default (Organization)'
                                                            }
                                                        >
                                                            <Select
                                                                onChange={(key) => {
                                                                    const s = paymentTermsSelectorKeyToStructured(key);
                                                                    handleCustomerOnChange({ name: 'payment_terms_type', value: s.payment_terms_type });
                                                                    handleCustomerOnChange({ name: 'payment_terms_days', value: s.payment_terms_days });
                                                                    handleCustomerOnChange({ name: 'payment_terms_eom_offset_months', value: s.payment_terms_eom_offset_months });
                                                                    form.setFieldsValue({
                                                                        payment_terms_type: s.payment_terms_type,
                                                                        payment_terms_days: s.payment_terms_days,
                                                                        payment_terms_eom_offset_months: s.payment_terms_eom_offset_months,
                                                                    });
                                                                }}
                                                                options={[
                                                                    {
                                                                        value: PAYMENT_TERMS_SELECTOR_ORG,
                                                                        label:
                                                                            customer?.extra?.meta?.sale_due_date_days != null
                                                                                ? `Default (Organization: Net ${customer.extra.meta.sale_due_date_days})`
                                                                                : 'Default (Organization)',
                                                                    },
                                                                    { value: 'days_0', label: 'Due on receipt' },
                                                                    { value: 'days_7', label: 'Net 7' },
                                                                    { value: 'days_15', label: 'Net 15' },
                                                                    { value: 'days_30', label: 'Net 30' },
                                                                    { value: 'days_45', label: 'Net 45' },
                                                                    { value: 'days_60', label: 'Net 60' },
                                                                    { value: 'eom_0', label: 'End of month (EOM)' },
                                                                    { value: 'eom_1', label: 'End of next month (EOM+1)' },
                                                                ]}
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            label="Credit Limit"
                                                            name="credit_limit"
                                                            rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: 'Please enter a valid amount' }]}
                                                        >
                                                            <Input
                                                                name="credit_limit"
                                                                type="number"
                                                                onChange={({ target }) => handleCustomerOnChange(target)}
                                                                prefix="$"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Fragment>
                                        ),
                                    },
                                ]
                                : []),
                            {
                                key: 'websites',
                                label: (
                                    <span>
                                        Websites
                                    </span>
                                ),
                                children: (
                                    <Fragment>
                                        <Alert
                                            className='mb-3 ant-alert-light-gray'
                                            title="Add your customer's website and social media profiles to stay connected and build stronger relationships."
                                            icon={<PiShootingStarDuotone  />}
                                            showIcon={true}

                                        />
                                        <WebPages 
                                            name="web_pages"
                                            initialValues={web_pages}
                                        />
                                    </Fragment>
                                )
                            },
                            {
                                key: 'shipping',
                                label: (
                                    <span>
                                        Shipping
                                    </span>
                                ),
                                children: (
                                    <Fragment>
                                        {(!customer?.shippings?.length || !customer?.shippings?.[0]?.id) ? (
                                            <Fragment>
                                                <Alert
                                                    title={!displayShippingAccount
                                                        ? "Shipping account is the same as primary account. Uncheck if you would like to add a shipping account."
                                                        : "If your customer's shipping information is different from its primary account, complete the form below and make sure to save it."}
                                                    type={!displayShippingAccount ? "success" : "info"}
                                                />
                                                <Checkbox
                                                    className='mt-3 mb-3'
                                                    checked={!displayShippingAccount}
                                                    onChange={(e) => setDisplayShippingAccount(!e.target.checked)}
                                                >
                                                    Shipping Account is the same as Primary Account
                                                </Checkbox>

                                                {displayShippingAccount && (
                                                    <CustomerShippingForm
                                                        ref={shippingFormRef}
                                                        customer={customer}
                                                        shipping={customer.shippings && customer.shippings.length > 0 ? customer.shippings[0] : undefined}
                                                        idMain={customer.id}
                                                        customerFormCallBack={customerFormCallBack}
                                                    />
                                                )}
                                            </Fragment>
                                        ) : (
                                            <div>
                                                <Row justify="end">
                                                    <Popconfirm
                                                        title="Delete the shipping account"
                                                        description="Are you sure to delete this shipping account?"
                                                        onConfirm={() => {}} // TODO: Implement handleRemoveShipping
                                                        okText="Yes"
                                                        cancelText="No"
                                                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                    >
                                                        <Button danger>Delete shipping account</Button>
                                                    </Popconfirm>
                                                </Row>
                                                <CustomerShippingForm
                                                    ref={shippingFormRef}
                                                    customer={customer}
                                                    shipping={customer.shippings[0]}
                                                    idMain={customer.id}
                                                    customerFormCallBack={customerFormCallBack}
                                                />
                                            </div>
                                        )}
                                    </Fragment>
                                )
                            }
                        ]}
                    />

                    <Col>
                        <Divider titlePlacement="start" plain>Save all your data</Divider>
                        <Form.Item>
                            <p>Make sure to save the update before closing this page.</p>
                            <Button
                                type="primary"
                                size='default'
                                icon={<SaveOutlined className="fs-1" />}
                                htmlType="submit"
                            >
                                Save all information
                            </Button>
                        </Form.Item>
                    </Col>
                </Form>
            </div>
        </Fragment>
    );
};

export default CustomerForm;
